import { useState, useEffect, useRef } from 'react'
import { supabase } from '../supabaseClient'
import { getTeamLabel } from '../utils/teamLabels'
import { DEFAULT_LOGO, getClubLogo } from '../clubLogos'
import Logo from '../components/Logo'

const ACTIVE_PHASES = ['playing', 'ht', 'et1', 'et_ht', 'et2']
function isMatchLive(f) {
  return f.status === 'live' || f.clock_running || ACTIVE_PHASES.includes(f.match_phase)
}
function phaseShortLabel(f) {
  if (f.match_phase === 'ht') return 'HT'
  if (f.match_phase === 'et_ht') return 'ET HT'
  return null
}

function formatDate(dateStr) {
  if (!dateStr) return { month: '—', day: '—', time: '', full: '' }
  const d = new Date(dateStr)
  return {
    month: d.toLocaleString('en-IE', { month: 'short', timeZone: 'UTC' }),
    day: d.getUTCDate(),
    time: d.toLocaleString('en-IE', { hour: '2-digit', minute: '2-digit', timeZone: 'UTC' }),
    full: d.toLocaleString('en-IE', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' })
  }
}

const tabBtn = (active) => ({
  flex: 1, padding: '10px 4px', background: 'none', border: 'none', cursor: 'pointer',
  fontSize: '11px', fontWeight: '500',
  color: active ? '#185FA5' : '#888',
  borderBottom: active ? '2px solid #185FA5' : '2px solid transparent'
})

const filterBtn = (active) => ({
  padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer',
  fontSize: '11px', fontWeight: '500',
  background: active ? '#185FA5' : '#e8e8e8',
  color: active ? 'white' : '#555'
})

export default function PublicSquad() {
  const slug = window.location.pathname.replace('/public/', '').replace(/\/$/, '')
  const [squad, setSquad] = useState(null)
  const [fixtures, setFixtures] = useState([])
const [liveFixtures, setLiveFixtures] = useState([])
  const [liveFixture, setLiveFixture] = useState(null) // active live view
  const [loading, setLoading] = useState(true)
  const [notFound, setNotFound] = useState(false)
  const [activeTab, setActiveTab] = useState('fixtures')
  const [teamFilter, setTeamFilter] = useState('all')
  const [sportFilter, setSportFilter] = useState('all')
  const publicUrl = 'gaamatchtracker.ie/public/' + slug

  useEffect(() => { loadData() }, [])

    useEffect(() => {
    if (liveFixtures.length === 0) return
    const interval = setInterval(() => loadLiveFixtures(), 15000)
    return () => clearInterval(interval)
  }, [liveFixtures.length])

  async function loadLiveFixtures() {
    const { data } = await supabase.from('fixtures')
      .select('*').eq('squad_id', liveFixtures[0]?.squad_id).eq('status', 'live')
    if (data) setLiveFixtures(data)
  }

  async function loadData() {
    const { data: squadData } = await supabase
    .from('squads').select('*, clubs(club_name, name, slug), squad_teams(team_number, label)')
      .eq('slug', slug).maybeSingle()
    if (!squadData) { setNotFound(true); setLoading(false); return }
    setSquad(squadData)
    const { data: fixturesData } = await supabase
      .from('fixtures').select('*')
      .eq('squad_id', squadData.id)
      .order('fixture_date', { ascending: false })
    setFixtures(fixturesData || [])
    const live = (fixturesData || []).filter(f => f.status === 'live')
    setLiveFixtures(live)
    if (live.length > 0) setLiveFixture(live[0])
    setLoading(false)
  }

  async function loadLiveFixture(id) {
    const { data } = await supabase.from('fixtures').select('*').eq('id', id).single()
    if (data) setLiveFixture(data)
  }

  if (loading) return (
    <div style={{ minHeight: '100vh', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
      <p style={{ color: 'white', fontSize: '16px' }}>Loading...</p>
    </div>
  )

  if (notFound) return (
    <div style={{ minHeight: '100vh', background: '#185FA5', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
      <div style={{ background: 'white', borderRadius: '16px', padding: '32px', textAlign: 'center', maxWidth: '340px' }}>
        <p style={{ fontSize: '48px', marginBottom: '16px' }}>🏐</p>
        <h2 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '8px' }}>Squad not found</h2>
        <p style={{ fontSize: '14px', color: '#888' }}>This link may be invalid or the squad has been removed.</p>
      </div>
    </div>
  )

  const clubName = squad.clubs?.club_name || squad.clubs?.name || ''
const allUpcoming = fixtures.filter(f => f.status === 'upcoming' || f.status === 'live').sort((a, b) => new Date(a.fixture_date) - new Date(b.fixture_date))
  const allCompleted = fixtures.filter(f => f.status === 'completed').sort((a, b) => new Date(b.fixture_date) - new Date(a.fixture_date))

  const applyFilters = (list) => list.filter(f => {
    const teamMatch = teamFilter === 'all' || String(f.team_number || 1) === teamFilter
    const sportMatch = sportFilter === 'all' || f.sport === sportFilter
    return teamMatch && sportMatch
  })

  const upcoming = applyFilters(allUpcoming)
  const completed = applyFilters(allCompleted)
  const nextFixture = allUpcoming[0]

  // Detect available teams/sports
  const hasTeam2 = fixtures.some(f => f.team_number === 2 || f.team_number === '2')
  const hasHurling = fixtures.some(f => f.sport === 'hurling')
  const hasFootball = fixtures.some(f => f.sport === 'football')

  const sportLabel = (s) => s === 'hurling' ? '🥍 Hurling' : '⚽ Football'
  const teamLabel = (n) => 'Team ' + (n || 1)

  return (
    <div style={{ minHeight: '100vh', background: '#f5f5f5' }}>
      {/* Header */}
      <div style={{ background: '#185FA5', color: 'white', padding: '16px', textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'flex-start', padding: '0 0 8px' }}>
          <a href="https://gaamatchtracker.ie" style={{ textDecoration: 'none' }}>
            <Logo variant="light" width="120px" maxWidth="120px" />
          </a>
        </div>
        <img src={getClubLogo(clubName)} alt="" style={{ width: '48px', height: '48px', borderRadius: '50%', objectFit: 'contain', marginBottom: '8px' }} onError={e => { e.target.style.display = 'none' }} />
        <h1 style={{ fontSize: '18px', fontWeight: '600', margin: '0 0 2px' }}>{squad.name}</h1>
        <p style={{ fontSize: '13px', opacity: 0.8, margin: '0 0 6px' }}>{clubName}</p>
        <p style={{ fontSize: '10px', opacity: 0.5, margin: 0, letterSpacing: '0.3px' }}>🔗 {publicUrl}</p>
        {squad.clubs?.slug && (
          <a href={`/public/club/${squad.clubs.slug}`}
            style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)', marginTop: '6px', display: 'block', textDecoration: 'none' }}>
            View all {clubName} fixtures →
          </a>
        )}
      </div>

      {/* Live banner — fixtures page only */}
        {liveFixtures.filter(isMatchLive).length > 0 && activeTab !== 'live' && liveFixtures.filter(isMatchLive).map(f => (
        <div key={f.id} onClick={() => { setLiveFixture(f); setActiveTab('live') }}
          style={{ background: '#A32D2D', color: 'white', padding: '10px 16px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer', borderBottom: '0.5px solid rgba(255,255,255,0.2)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <img src={getClubLogo(clubName)} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
            <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }}></span>
            <div>
              <span style={{ fontSize: '12px', fontWeight: '500' }}>
                {phaseShortLabel(f) ? phaseShortLabel(f) + ' vs ' + f.opponent : 'LIVE vs ' + f.opponent}
              </span>
            <span style={{ fontSize: '10px', opacity: 0.8, marginLeft: '6px' }}>{getTeamLabel(f.team_number || 1, squad?.squad_teams)} · {f.sport === 'hurling' ? 'Hurling' : 'Football'}</span>
            </div>
            <img src={getClubLogo(f.opponent)} alt="" style={{ width: '24px', height: '24px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.style.display = 'none' }} />
          </div>
          <span style={{ fontSize: '15px', fontWeight: '700' }}>
            {f.home_goals}-{String(f.home_points).padStart(2, '0')} · {f.away_goals}-{String(f.away_points).padStart(2, '0')}
          </span>
          <span style={{ fontSize: '11px', opacity: 0.9 }}>Watch →</span>
        </div>
      ))}

      {/* Next fixture banner */}
      {nextFixture && activeTab !== 'live' && (
        <div style={{ background: '#E6F1FB', padding: '10px 16px', borderBottom: '0.5px solid #c8dff5' }}>
          <p style={{ fontSize: '10px', color: '#185FA5', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '3px' }}>NEXT FIXTURE</p>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <p style={{ fontSize: '13px', fontWeight: '500', color: '#185FA5' }}>vs {nextFixture.opponent}</p>
              <p style={{ fontSize: '11px', color: '#555' }}>
                {formatDate(nextFixture.fixture_date).full} · {formatDate(nextFixture.fixture_date).time}
                {nextFixture.venue ? ' · ' + nextFixture.venue : ''}
                {' · '}{sportLabel(nextFixture.sport)} · {teamLabel(nextFixture.team_number)}
              </p>
            </div>
            {nextFixture.venue_url && (
              <a href={nextFixture.venue_url} target="_blank" rel="noopener noreferrer"
                style={{ fontSize: '11px', padding: '5px 10px', borderRadius: '8px', background: '#185FA5', color: 'white', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                📍 Map
              </a>
            )}
          </div>
        </div>
      )}

      {/* Tabs */}
      {activeTab !== 'live' && (
        <div style={{ display: 'flex', background: 'white', borderBottom: '0.5px solid #e0e0e0' }}>
          {[
            { id: 'fixtures', label: '📅 Fixtures' },
            { id: 'results', label: '🏆 Results' },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)} style={tabBtn(activeTab === t.id)}>
              {t.label}
            </button>
          ))}
        </div>
      )}

      {/* Filters */}
        {activeTab !== 'live' && (hasTeam2 || hasHurling) && (
        <div style={{ background: 'white', padding: '8px 16px', borderBottom: '0.5px solid #e0e0e0', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {hasTeam2 && (
            <>
              <button style={filterBtn(teamFilter === 'all')} onClick={() => setTeamFilter('all')}>All Teams</button>
              <button style={filterBtn(teamFilter === '1')} onClick={() => setTeamFilter('1')}>{getTeamLabel(1, squad?.squad_teams)}</button>
              <button style={filterBtn(teamFilter === '2')} onClick={() => setTeamFilter('2')}>{getTeamLabel(2, squad?.squad_teams)}</button>
            </>
          )}
          {hasHurling && hasFootball && (
            <>
              <button style={filterBtn(sportFilter === 'all')} onClick={() => setSportFilter('all')}>All Sports</button>
              <button style={filterBtn(sportFilter === 'football')} onClick={() => setSportFilter('football')}>Football</button>
              <button style={filterBtn(sportFilter === 'hurling')} onClick={() => setSportFilter('hurling')}>Hurling</button>
            </>
          )}
        </div>
      )}

      {/* Fixtures tab */}
      {activeTab === 'fixtures' && (
        <div style={{ padding: '12px 16px' }}>
          {upcoming.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '32px', fontSize: '14px' }}>No upcoming fixtures</p>
          ) : upcoming.map(f => {
            const date = formatDate(f.fixture_date)
            const oppLogo = getClubLogo(f.opponent)
            return (
              <div key={f.id} style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                  <div style={{ textAlign: 'center', minWidth: '32px' }}>
                    <p style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>{date.month}</p>
                    <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1 }}>{date.day}</p>
                  </div>
                  <img src={oppLogo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
                  <div style={{ flex: 1 }}>
                    <p style={{ fontSize: '13px', fontWeight: '500' }}>vs {f.opponent}</p>
                    <p style={{ fontSize: '11px', color: '#888' }}>{date.time}{f.venue ? ' · ' + f.venue : ''}</p>
                    <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
                        {sportLabel(f.sport)}
                      </span>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
                        {teamLabel(f.team_number)}
                      </span>
                      <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
                        {f.game_type === 'league' ? 'League' : f.game_type === 'championship' ? 'Championship' : 'Friendly'}
                      </span>
                    </div>
                  </div>
                  {f.venue_url && (
                    <a href={f.venue_url} target="_blank" rel="noopener noreferrer"
                      style={{ fontSize: '11px', padding: '5px 8px', borderRadius: '8px', background: '#E6F1FB', color: '#185FA5', textDecoration: 'none', whiteSpace: 'nowrap' }}>
                      📍 Map
                    </a>
                  )}
                </div>
              </div>
            )
          })}
        </div>
      )}

      {/* Results tab */}
      {activeTab === 'results' && (
        <div style={{ padding: '12px 16px' }}>
          {completed.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#888', padding: '32px', fontSize: '14px' }}>No results yet</p>
          ) : completed.map(f => {
            const date = formatDate(f.fixture_date)
            const oppLogo = getClubLogo(f.opponent)
            const ours = f.home_goals * 3 + f.home_points
            const theirs = f.away_goals * 3 + f.away_points
            const result = ours > theirs ? 'W' : ours < theirs ? 'L' : 'D'
            const resultColor = result === 'W' ? '#3B6D11' : result === 'L' ? '#A32D2D' : '#888'
            const resultBg = result === 'W' ? '#EAF3DE' : result === 'L' ? '#FCEBEB' : '#f8f8f8'
            return (
              <ResultCard key={f.id} fixture={f} date={date} oppLogo={oppLogo}
                ours={ours} theirs={theirs} result={result}
                resultColor={resultColor} resultBg={resultBg}
                sportLabel={sportLabel} teamLabel={teamLabel} />
            )
          })}
        </div>
      )}


      {/* Live view */}
      {activeTab === 'live' && liveFixture && (
        <LiveView fixture={liveFixture} onBack={() => setActiveTab('fixtures')} squadName={squad.name} />
      )}

      {/* Footer */}
      <div style={{ padding: '20px', textAlign: 'center', borderTop: '0.5px solid #e0e0e0', marginTop: '20px' }}>
        <p style={{ fontSize: '11px', color: '#aaa', marginBottom: '4px' }}>Powered by</p>
        <a href="https://gaamatchtracker.ie" style={{ fontSize: '12px', color: '#185FA5', fontWeight: '500' }}>GAA Match Tracker</a>
      </div>
    </div>
  )
}

function ResultCard({ fixture: f, date, oppLogo, ours, theirs, result, resultColor, resultBg, sportLabel, teamLabel }) {
  const [scorers, setScorers] = useState([])

  useEffect(() => {
    async function load() {
      const { data } = await supabase.from('match_events')
        .select('player_id, event_type, players(full_name)')
        .eq('fixture_id', f.id)
        .in('event_type', ['goal', 'point', 'twopoint'])
      if (!data) return
      const tally = {}
      data.forEach(e => {
        const key = e.player_id || 'unknown'
        const name = e.players?.full_name || 'Unknown'
        if (!tally[key]) tally[key] = { name, goals: 0, points: 0 }
        if (e.event_type === 'goal') tally[key].goals++
        if (e.event_type === 'point') tally[key].points++
        if (e.event_type === 'twopoint') tally[key].points += 2
      })
      setScorers(Object.values(tally).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points)))
    }
    load()
  }, [f.id])

  return (
    <div style={{ background: 'white', borderRadius: '12px', padding: '14px', marginBottom: '10px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
        <div style={{ textAlign: 'center', minWidth: '32px' }}>
          <p style={{ fontSize: '10px', color: '#888', fontWeight: '500' }}>{date.month}</p>
          <p style={{ fontSize: '20px', fontWeight: '700', color: '#185FA5', lineHeight: 1 }}>{date.day}</p>
        </div>
        <img src={oppLogo} alt="" style={{ width: '34px', height: '34px', borderRadius: '50%', objectFit: 'contain' }} onError={e => { e.target.src = DEFAULT_LOGO }} />
        <div style={{ flex: 1 }}>
          <p style={{ fontSize: '13px', fontWeight: '500' }}>vs {f.opponent}</p>
          <p style={{ fontSize: '13px', fontWeight: '600', color: '#333' }}>
            {f.home_goals}-{String(f.home_points).padStart(2, '0')} · {f.away_goals}-{String(f.away_points).padStart(2, '0')}
          </p>
          <p style={{ fontSize: '11px', color: '#999' }}>({ours} pts) vs ({theirs} pts)</p>
          <div style={{ display: 'flex', gap: '4px', marginTop: '3px', flexWrap: 'wrap' }}>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#E6F1FB', color: '#185FA5' }}>
              {sportLabel(f.sport)}
            </span>
            <span style={{ fontSize: '10px', padding: '2px 6px', borderRadius: '10px', background: '#f0f0f0', color: '#555' }}>
              {teamLabel(f.team_number)}
            </span>
          </div>
        </div>
        <span style={{ fontSize: '13px', fontWeight: '700', padding: '4px 10px', borderRadius: '20px', background: resultBg, color: resultColor }}>
          {result}
        </span>
      </div>
      {scorers.length > 0 && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '0.5px solid #f0f0f0' }}>
          <p style={{ fontSize: '10px', color: '#aaa', marginBottom: '4px', fontWeight: '600' }}>SCORERS</p>
          <p style={{ fontSize: '12px', color: '#555' }}>
            {scorers.map(s => s.name + ' ' + s.goals + '-' + String(s.points).padStart(2, '0')).join(' · ')}
          </p>
        </div>
      )}
      {f.match_report && f.match_report_published && (
        <div style={{ marginTop: '10px', paddingTop: '10px', borderTop: '0.5px solid #f0f0f0' }}>
          
            <a
            href={'/public/report/' + f.id}
            target="_blank"
            rel="noreferrer"
            style={{ display: 'block', width: '100%', padding: '9px 0', background: '#f0f5ff', color: '#185FA5', border: 'none', borderRadius: 8, cursor: 'pointer', fontWeight: 600, fontSize: 13, textAlign: 'center', textDecoration: 'none' }}
          >
            📰 Read Match Report →
          </a>
        </div>
      )}
    </div>
  )
}

function TopScorers({ squadId, fixtures, hasTeam2, hasHurling, hasFootball, team1Label, team2Label }) {
  const [activeTab, setActiveTab] = useState(null)
  const [scorersByCategory, setScorersByCategory] = useState({})
  const [loading, setLoading] = useState(true)

  const categories = []
  if (hasTeam2) {
    if (hasFootball) categories.push({ key: '1-football', label: (team1Label || 'Team 1') + ' Football' })
    if (hasHurling) categories.push({ key: '1-hurling', label: (team1Label || 'Team 1') + ' Hurling' })
    if (hasFootball) categories.push({ key: '2-football', label: 'Team 2 Football' })
    if (hasHurling) categories.push({ key: '2-hurling', label: 'Team 2 Hurling' })
  } else {
    if (hasFootball) categories.push({ key: '1-football', label: 'Football' })
    if (hasHurling) categories.push({ key: '1-hurling', label: 'Hurling' })
    if (!hasFootball && !hasHurling) categories.push({ key: '1-football', label: 'All' })
  }

  useEffect(() => {
    if (categories.length > 0 && !activeTab) setActiveTab(categories[0].key)
  }, [categories.length])

  useEffect(() => {
    if (!activeTab) return
    loadScorers(activeTab)
  }, [activeTab])

  async function loadScorers(catKey) {
    if (scorersByCategory[catKey]) return
    const [team, sport] = catKey.split('-')
    const completedFixtures = fixtures.filter(f =>
      f.status === 'completed' &&
      String(f.team_number || 1) === team &&
      (sport === 'all' || f.sport === sport)
    )
    if (completedFixtures.length === 0) {
      setScorersByCategory(prev => ({ ...prev, [catKey]: [] }))
      setLoading(false)
      return
    }
    const ids = completedFixtures.map(f => f.id)
    const { data: events } = await supabase
      .from('match_events')
      .select('player_id, event_type, players(full_name)')
      .in('fixture_id', ids)
      .in('event_type', ['goal', 'point', 'twopoint'])
    const tally = {}
    ;(events || []).forEach(e => {
      const key = e.player_id || 'unknown'
      const name = e.players?.full_name || 'Unknown'
      if (!tally[key]) tally[key] = { name, goals: 0, points: 0 }
      if (e.event_type === 'goal') tally[key].goals++
      if (e.event_type === 'point') tally[key].points++
      if (e.event_type === 'twopoint') tally[key].points += 2
    })
    const sorted = Object.values(tally).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points))
    setScorersByCategory(prev => ({ ...prev, [catKey]: sorted }))
    setLoading(false)
  }

  const currentScorers = activeTab ? (scorersByCategory[activeTab] || []) : []

  return (
    <div>
      {categories.length > 1 && (
        <div style={{ display: 'flex', background: 'white', borderBottom: '0.5px solid #e0e0e0', overflowX: 'auto' }}>
          {categories.map(c => (
            <button key={c.key} onClick={() => setActiveTab(c.key)}
              style={{ ...tabBtn(activeTab === c.key), whiteSpace: 'nowrap', padding: '10px 14px' }}>
              {c.label}
            </button>
          ))}
        </div>
      )}
      <div style={{ padding: '12px 16px' }}>
        {loading && !scorersByCategory[activeTab] ? (
          <p style={{ textAlign: 'center', padding: '32px', color: '#888' }}>Loading...</p>
        ) : currentScorers.length === 0 ? (
          <p style={{ textAlign: 'center', padding: '32px', color: '#888', fontSize: '14px' }}>No scores recorded yet</p>
        ) : currentScorers.map((s, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '12px', padding: '12px 16px', marginBottom: '8px', display: 'flex', alignItems: 'center', gap: '12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <span style={{ fontSize: '15px', fontWeight: '700', minWidth: '24px', color: i === 0 ? '#854F0B' : i === 1 ? '#888' : i === 2 ? '#3B6D11' : '#bbb' }}>
              {i + 1}
            </span>
            <span style={{ flex: 1, fontSize: '14px', fontWeight: '500' }}>{s.name}</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color: '#185FA5' }}>
              {s.goals}-{String(s.points).padStart(2, '0')}
            </span>
            <span style={{ fontSize: '11px', color: '#888' }}>({s.goals * 3 + s.points} pts)</span>
          </div>
        ))}
      </div>
    </div>
  )
}

function LiveView({ fixture, onBack, squadName }) {
  const [liveData, setLiveData] = useState(fixture)
  const [events, setEvents] = useState([])
  const [seconds, setSeconds] = useState(0)
  const [half, setHalf] = useState(1)
  const clockRef = useRef(null)

  useEffect(() => {
    loadEvents()
    syncClock()
    const pollInterval = setInterval(() => { syncClock(); loadEvents() }, 10000)
    return () => { clearInterval(pollInterval); if (clockRef.current) clearInterval(clockRef.current) }
  }, [])

    async function syncClock() {
        const { data } = await supabase.from('fixtures')
        .select('home_goals, home_points, away_goals, away_points, clock_started_at, clock_seconds_at_start, clock_running, clock_half, match_phase, status, sport, team_number')
        .eq('id', fixture.id).single()
    if (!data) return
    setLiveData(data)
    setHalf(data.clock_half || 1)

    if (clockRef.current) clearInterval(clockRef.current)

    if (data.clock_running && data.clock_started_at) {
      const startEpoch = new Date(data.clock_started_at).getTime()
      const base = data.clock_seconds_at_start || 0
      const tick = () => {
        const elapsed = Math.floor((Date.now() - startEpoch) / 1000)
        setSeconds(Math.min(base + elapsed, 3000))
      }
      tick()
      clockRef.current = setInterval(tick, 1000)
    } else {
      setSeconds(data.clock_seconds_at_start || 0)
    }
  }

  async function loadEvents() {
    const { data } = await supabase.from('match_events')
      .select('*, players(full_name)')
      .eq('fixture_id', fixture.id)
      .in('event_type', ['goal', 'point', 'twopoint', 'wide', 'opp_wide', 'fortyfive_won', 'fortyfive_conceded', 'kickout_won', 'kickout_lost', 'opp_kickout_won', 'opp_kickout_lost', 'free_won', 'free_conceded'])
      .order('minute', { ascending: true })
    setEvents(data || [])
  }

  const totalHome = liveData.home_goals * 3 + liveData.home_points
  const totalAway = liveData.away_goals * 3 + liveData.away_points
  const diff = Math.abs(totalHome - totalAway)
  const leadText = totalHome > totalAway ? 'Leading by ' + diff + ' pt' + (diff !== 1 ? 's' : '')
    : totalAway > totalHome ? fixture.opponent + ' leading by ' + diff + ' pt' + (diff !== 1 ? 's' : '')
    : 'Level'
  const clockDisplay = String(Math.floor(seconds / 60)).padStart(2, '0') + ':' + String(seconds % 60).padStart(2, '0')

  const scorerTallies = {}
  events.forEach(e => {
    if (!['goal', 'point', 'twopoint'].includes(e.event_type)) return
    const key = e.player_id || 'unknown'
    const name = e.players?.full_name || 'Unknown'
    if (!scorerTallies[key]) scorerTallies[key] = { name, goals: 0, points: 0 }
    if (e.event_type === 'goal') scorerTallies[key].goals++
    if (e.event_type === 'point') scorerTallies[key].points++
    if (e.event_type === 'twopoint') scorerTallies[key].points += 2
  })
  const scorerList = Object.values(scorerTallies).sort((a, b) => (b.goals * 3 + b.points) - (a.goals * 3 + a.points))

  const ourWides = events.filter(e => e.event_type === 'wide').length
  const oppWides = events.filter(e => e.event_type === 'opp_wide').length
  const ourFortyFives = events.filter(e => e.event_type === 'fortyfive_won').length
  const oppFortyFives = events.filter(e => e.event_type === 'fortyfive_conceded').length
  const ourKOWon = events.filter(e => e.event_type === 'kickout_won').length
  const ourKOLost = events.filter(e => e.event_type === 'kickout_lost').length
  const freesWon = events.filter(e => e.event_type === 'free_won').length
  const freesConceded = events.filter(e => e.event_type === 'free_conceded').length

  const sport = liveData.sport || fixture.sport || 'football'
  const teamNum = liveData.team_number || fixture.team_number || 1
  const sportLabel = sport === 'hurling' ? '🥍 Hurling' : '⚽ Football'

  return (
    <div>
      <button onClick={onBack}
        style={{ display: 'block', width: '100%', padding: '10px 16px', background: '#f0f0f0', border: 'none', fontSize: '13px', color: '#555', cursor: 'pointer', textAlign: 'left' }}>
        ← Back to fixtures
      </button>

      {/* Match info */}
      <div style={{ background: '#0d3d6b', color: 'white', padding: '6px 16px', display: 'flex', justifyContent: 'center', gap: '12px' }}>
        <span style={{ fontSize: '11px', opacity: 0.8 }}>{sportLabel}</span>
        <span style={{ fontSize: '11px', opacity: 0.5 }}>·</span>
        <span style={{ fontSize: '11px', opacity: 0.8 }}>Team {teamNum}</span>
      </div>

      {/* Score bar */}
      <div style={{ background: '#185FA5', color: 'white', padding: '16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' }}>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>{squadName}</p>
            <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '-1px', lineHeight: 1 }}>
              {liveData.home_goals}-{String(liveData.home_points).padStart(2, '0')}
            </p>
            <p style={{ fontSize: '11px', opacity: 0.7 }}>({totalHome} pts)</p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '5px', marginBottom: '4px' }}>
              <span style={{ width: '7px', height: '7px', borderRadius: '50%', background: '#ff6b6b', display: 'inline-block' }}></span>
            <p style={{ fontSize: '11px', opacity: 0.8 }}>
                {liveData.match_phase === 'ht' ? '⏸ Half Time' :
                 liveData.match_phase === 'ft' ? '🏁 Full Time' :
                 liveData.match_phase === 'et1' ? 'ET 1st Half' :
                 liveData.match_phase === 'et_ht' ? '⏸ ET Half Time' :
                 liveData.match_phase === 'et2' ? 'ET 2nd Half' :
                 liveData.match_phase === 'et_ft' ? '🏁 ET Full Time' :
                 half === 1 ? '1st Half' : half === 2 ? '2nd Half' :
                 half === 3 ? 'ET 1st Half' : 'ET 2nd Half'}
              </p>
            </div>
            <p style={{ fontSize: '24px', fontWeight: '600', fontVariantNumeric: 'tabular-nums' }}>
              {['ht', 'ft', 'et_ht', 'et_ft'].includes(liveData.match_phase) ? '00:00' : clockDisplay}
            </p>
          </div>
          <div style={{ textAlign: 'center', flex: 1 }}>
            <p style={{ fontSize: '11px', opacity: 0.8, marginBottom: '4px' }}>{fixture.opponent}</p>
            <p style={{ fontSize: '36px', fontWeight: '700', letterSpacing: '-1px', lineHeight: 1 }}>
              {liveData.away_goals}-{String(liveData.away_points).padStart(2, '0')}
            </p>
            <p style={{ fontSize: '11px', opacity: 0.7 }}>({totalAway} pts)</p>
          </div>
        </div>
        <div style={{ textAlign: 'center', fontSize: '12px', background: 'rgba(255,255,255,0.15)', borderRadius: '20px', padding: '4px 12px' }}>
          {leadText}
        </div>
      </div>

      {/* Scorers */}
      {scorerList.length > 0 && (
        <div style={{ background: 'white', margin: '12px 16px', borderRadius: '12px', padding: '14px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
          <p style={{ fontSize: '11px', color: '#888', fontWeight: '600', letterSpacing: '0.5px', marginBottom: '10px' }}>SCORERS</p>
          {scorerList.map((s, i) => (
            <div key={i} style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '4px 0', borderBottom: '0.5px solid #f5f5f5' }}>
              <span style={{ fontSize: '13px', flex: 1 }}>{s.name}</span>
              <span style={{ fontSize: '13px', fontWeight: '600', color: '#185FA5' }}>
                {s.goals}-{String(s.points).padStart(2, '0')}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Subtotals */}
      <div style={{ margin: '0 16px 12px', display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
        {[
          { label: 'Wides', us: ourWides, them: oppWides },
          { label: '45s / 65s', us: ourFortyFives, them: oppFortyFives },
          { label: 'Kick Outs Won', us: ourKOWon, them: ourKOLost },
          { label: 'Frees', us: freesWon, them: freesConceded },
        ].map((stat, i) => (
          <div key={i} style={{ background: 'white', borderRadius: '10px', padding: '10px 12px', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
            <p style={{ fontSize: '10px', color: '#888', marginBottom: '4px' }}>{stat.label}</p>
            <p style={{ fontSize: '14px', fontWeight: '600', color: '#185FA5' }}>
              {stat.us} <span style={{ color: '#aaa', fontWeight: '400' }}>— {stat.them}</span>
            </p>
          </div>
        ))}
      </div>

      <p style={{ textAlign: 'center', fontSize: '11px', color: '#aaa', padding: '8px' }}>Live · updates every 10s</p>
    </div>
  )
}